import { createBrowserRouter, Navigate } from "react-router-dom";
import ProtectedRoute, { OnboardingRoute } from "../ProtectedRoute";
import { PublicRouteGuard } from "../PublicRouteGuard";
import { MainContentLayout } from "../MainLayout";
import { PublicLayout } from "../PublicLayout";
import { routesConfig } from "./routesConfig";
import MinimalLayout from "../MinimalLayout";
import Providers from "@src/providers";
import ErrorPage from "@src/presentation/pages/error-pages/ErrorPage";
import NotFound from "@src/presentation/pages/error-pages/NotFound";
import Unauthorized from "@src/presentation/pages/error-pages/Unauthorized";
import { RouteGuard } from "@src/presentation/components/RouteGuard";

// Admin routes - require auth and use MainContentLayout (with sidebar)
function getAdminRoutes() {
  const isAdminSite = import.meta.env.VITE_IS_ADMIN_SITE === 'true';
  const sharedRoutes = ['/profile', '/account/settings', '/account/security', '/notifications'];

  return {
    element: (
      <Providers>
        <ProtectedRoute />
      </Providers>
    ),
    children: [
      {
        path: "/",
        element: <Navigate to="/dashboard" replace />,
      },
      {
        element: <MainContentLayout />,
        errorElement: <ErrorPage />,
        children: [
          ...routesConfig
            .filter((r) => (r.protected || (isAdminSite && (r.path === '/pascos/:id' || sharedRoutes.includes(r.path)))) && !r.isDisabled)
            .map((r) => ({
              path: r.path,
              element: (
                <RouteGuard
                  permission={r.permission}
                  requiredPermissions={r.requiredPermissions}
                  anyPermissions={r.anyPermissions}
                >
                  {r.element}
                </RouteGuard>
              ),
            })),
          {
            path: "*",
            element: <NotFound />,
          },
        ],
      },
    ],
  };
}

// Shared authenticated routes (profile, settings, etc.) - use PublicLayout
function getSharedAuthRoutes() {
  const isAdminSite = import.meta.env.VITE_IS_ADMIN_SITE === 'true';
  const sharedRoutes = ['/profile', '/account/settings', '/account/security', '/notifications'];

  return {
    element: (
      <Providers>
        <ProtectedRoute />
      </Providers>
    ),
    children: [
      {
        element: <PublicLayout />,
        errorElement: <ErrorPage />,
        children: [
          ...routesConfig
            .filter((r) => r.protected && !r.isDisabled && sharedRoutes.includes(r.path) && !isAdminSite)
            .map((r) => ({
              path: r.path,
              element: r.element,
            })),
          {
            path: "*",
            element: <NotFound />,
          },
        ],
      },
    ],
  };
}

// Onboarding routes - require auth but profile must be incomplete
function getOnboardingRoutes() {
  return {
    element: (
      <Providers>
        <OnboardingRoute />
      </Providers>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        element: <MinimalLayout />,
        children: [
          ...routesConfig
            .filter((r) => r.isOnboarding && !r.isDisabled)
            .map((r) => ({
              path: r.path,
              element: r.element,
            })),
        ],
      },
    ],
  };
}

// Auth routes (login, etc.) - use MinimalLayout
function getAuthRoutes() {
  return {
    element: (
      <Providers>
        <MinimalLayout />
      </Providers>
    ),
    errorElement: <ErrorPage />,
    children: [
      ...routesConfig
        .filter((r) => !r.protected && !r.isPublic && !r.isOnboarding && !r.isDisabled)
        .map((r) => ({
          path: r.path,
          element: r.element,
        })),
      {
        path: "/unauthorized",
        element: <Unauthorized />,
      },
    ],
  };
}

// Public routes - accessible without authentication
// BUT authenticated users with incomplete profiles are redirected to onboarding
function getPublicRoutes() {
  const isAdminSite = import.meta.env.VITE_IS_ADMIN_SITE === 'true';

  return {
    element: (
      <Providers>
        <PublicRouteGuard />
      </Providers>
    ),
    children: [
      {
        element: <PublicLayout />,
        errorElement: <ErrorPage />,
        children: [
          ...routesConfig
            .filter((r) => !r.protected && r.isPublic && !r.isDisabled && !(isAdminSite && r.path === '/pascos/:id'))
            .map((r) => ({
              path: r.path,
              element: r.element,
            })),
          {
            path: "*",
            element: <NotFound />,
          },
        ],
      },
    ],
  };
}

export const HOME_ROUTE = routesConfig.find((r) => r.isHome)?.path || "/";
export const ADMIN_HOME_ROUTE = "/dashboard";

const isAdminSite = import.meta.env.VITE_IS_ADMIN_SITE === 'true';

export const router = createBrowserRouter([
  // Public routes (always available)
  getPublicRoutes(),
  // Auth routes (login, etc.)
  getAuthRoutes(),
  // Onboarding routes (require auth, incomplete profile)
  getOnboardingRoutes(),
  // Admin routes (only for admin sites)
  ...(isAdminSite ? [getAdminRoutes()] : []),
  // Shared authenticated routes (profile, settings, etc. - only for public sites)
  ...(isAdminSite ? [] : [getSharedAuthRoutes()]),
]);
